/** The walk's sequence over the open decisions, desk by desk (DESIGN §6). Pure: no React. Built only from the review-state functions. */
import { thesisMissing, type ContentCatalog, type DraftView } from "@bsim/contracts";
import type { DecisionFamily, DecisionId } from "@bsim/domain";
import { deskOrder } from "../labels";
import type { NextStep } from "../nextStep";
import { decisionReviewState, openDecisionsOf } from "../reviewState";

export type WalkStep =
  | { readonly kind: "decision"; readonly desk: DecisionFamily; readonly decisionId: DecisionId; readonly index: number; readonly of: number }
  | { readonly kind: "rationale"; readonly desk: DecisionFamily }
  | { readonly kind: "cards" }
  | { readonly kind: "thesis" };

/**
 * `hasCards`: the round HAS event cards (stable for the round); the cards step then always exists, answered or not.
 * `cardsOwed`: unanswered cards remain; used only to decide whether the cards step is still owed (`resumeStep`).
 */
export interface WalkInput { readonly draft: DraftView; readonly catalog: ContentCatalog; readonly hasCards: boolean; readonly cardsOwed: boolean; /** The address names the thesis step although no line is owed. */ readonly optionalThesis?: boolean }

/** "d:loans.ci.growth" | "r:loans" | "cards" | "thesis": the step as a web address carries it. */
export const stepKey = (s: WalkStep): string => (s.kind === "decision" ? `d:${s.decisionId}` : s.kind === "rationale" ? `r:${s.desk}` : s.kind);

export function walkSteps({ draft, catalog, hasCards, optionalThesis = false }: WalkInput): readonly WalkStep[] {
  const steps: WalkStep[] = deskOrder(catalog).flatMap((desk): WalkStep[] => {
    const ids = openDecisionsOf(draft, desk, catalog);
    if (ids.length === 0) return [];
    const decisions = ids.map((decisionId, i): WalkStep => ({ kind: "decision", desk, decisionId, index: i + 1, of: ids.length }));
    return draft.changedFamilies.includes(desk) ? [...decisions, { kind: "rationale", desk }] : decisions;
  });
  if (hasCards) steps.push({ kind: "cards" });
  // Owed when nothing changed; otherwise an optional last step that is there when the address names it (the guide's "Thesis line" lands on it) or a line is written.
  if (draft.thesis.required || (draft.thesis.text ?? "") !== "" || optionalThesis) steps.push({ kind: "thesis" });
  return steps;
}

/** The step a web address names, or null when it names none that exists now (another round's id, a typo, a decision this round does not open). */
export const parseStepKey = (key: string | null, steps: readonly WalkStep[]): WalkStep | null =>
  key === null ? null : steps.find((s) => stepKey(s) === key) ?? null;

/** A rationale, the cards or the thesis the player still owes. The cards step is owed while cards remain unanswered. */
const owed = (i: WalkInput, s: WalkStep): boolean =>
  s.kind === "rationale" ? i.draft.missingRationales.includes(s.desk) : s.kind === "cards" ? i.cardsOwed : s.kind === "thesis" ? thesisMissing(i.draft.thesis) : false;

/** Where the walk opens: the first To-review decision, else the first step still owed, else null (the walk is done: land on Review & sign). */
export function resumeStep(i: WalkInput): WalkStep | null {
  const steps = walkSteps(i);
  return steps.find((s) => s.kind === "decision" && decisionReviewState(i.draft, s.decisionId) === "toReview") ?? steps.find((s) => owed(i, s)) ?? null;
}

/** The step the address names, or, when it names none that exists now (a stale key, a rationale step gone after an undo), where the walk resumes. */
export const currentOrResume = (i: WalkInput, steps: readonly WalkStep[], key: string | null): WalkStep | null => parseStepKey(key, steps) ?? resumeStep(i);

export function stepAfter(steps: readonly WalkStep[], from: WalkStep): WalkStep | null {
  const at = steps.findIndex((s) => stepKey(s) === stepKey(from));
  return at < 0 ? null : steps[at + 1] ?? null;
}

/** The walk step a next-step action jumps to; the forecast and Review & sign are not walk steps. */
export function stepForNextStep(i: WalkInput, next: NextStep): WalkStep | null {
  const steps = walkSteps(i);
  const want = next.kind === "rationale" ? `r:${next.family}` : next.kind === "event" ? "cards" : next.kind === "thesis" ? "thesis" : null;
  return want === null ? null : parseStepKey(want, steps);
}
